import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { TCComponentCertifiedWeightInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { TcComponentInformation, ProductShipmentResponse } from "../component-details.model";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import useNotification from "../../../../../../../shared/hooks/useNotification";

interface ProductComponentProps {
  productInfoData: TCComponentCertifiedWeightInformation[];
  setProductInfoData: React.Dispatch<React.SetStateAction<TCComponentCertifiedWeightInformation[]>>;
  componentNo?: string; // Add componentNo prop
  associatedProductNo?: string; // Associated product article number
  productData?: ProductShipmentResponse[]; // Product data to get product certified weight
  rawMaterialInfoData?: any[]; // Component raw material info for validation display
  componentNetShippingWeight?: number | null; // Component net shipping weight
  certifiedRawMaterialCodes?: any[]; // Certified RM codes from parent (optional, will fetch if not provided)
  tcStandardList?: string[]; // TC standards from parent (optional, will fetch if not provided)
}

interface LabelGradeOption {
  label: string;
  value: string;
}

const ProductComponent: React.FC<ProductComponentProps> = ({
  productInfoData,
  setProductInfoData,
  componentNo = "", // Destructure componentNo prop
  associatedProductNo = "",
  productData = [],
  rawMaterialInfoData = [],
  componentNetShippingWeight = null,
  certifiedRawMaterialCodes: parentCertifiedRawMaterialCodes = [],
  tcStandardList: parentTcStandardList = [],
}) => {
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const { notify } = useNotification();
  const [productRawMaterialInfo, setProductRawMaterialInfo] = useState<any[]>([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>(parentCertifiedRawMaterialCodes);
  const [tcStandardList, setTcStandardList] = useState<string[]>(parentTcStandardList);
  
  // Refs to track previous values and prevent unnecessary API calls
  const lastFetchedProductRef = useRef<string>("");
  const hasFetchedCertifiedRMCodes = useRef<boolean>(false);
  const executeGetRef = useRef(executeGet);

  // Update ref when executeGet changes
  useEffect(() => {
    executeGetRef.current = executeGet;
  }, [executeGet]);

  // Track last fetched TE_ID to prevent re-fetching on same TE_ID
  const lastTeIdFetchedRef = useRef<string>("");

  // Update local state when parent props change
  useEffect(() => {
    if (parentCertifiedRawMaterialCodes.length > 0) {
      setCertifiedRawMaterialCodes(parentCertifiedRawMaterialCodes);
    }
  }, [parentCertifiedRawMaterialCodes]);

  useEffect(() => {
    if (parentTcStandardList.length > 0) {
      setTcStandardList(parentTcStandardList);
    }
  }, [parentTcStandardList]);

  // Fetch certified raw material codes and TC standards - only if not provided by parent
  useEffect(() => {
    // If parent provided the data, don't fetch
    if (parentCertifiedRawMaterialCodes.length > 0 && parentTcStandardList.length > 0) {
      return;
    }

    if (!TE_ID) {
      hasFetchedCertifiedRMCodes.current = false;
      lastTeIdFetchedRef.current = "";
      if (parentCertifiedRawMaterialCodes.length === 0) {
        setCertifiedRawMaterialCodes([]);
      }
      if (parentTcStandardList.length === 0) {
        setTcStandardList([]);
      }
      return;
    }

    // Skip if we've already fetched for this TE_ID
    if (hasFetchedCertifiedRMCodes.current && lastTeIdFetchedRef.current === TE_ID) {
      return;
    }
    
    const fetchCertifiedData = async () => {
      try {
        hasFetchedCertifiedRMCodes.current = true;
        lastTeIdFetchedRef.current = TE_ID;
        
        const [rmRes, baseRes] = await Promise.all([
          executeGetRef.current(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES_V4),
          executeGetRef.current(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID }),
        ]);
        
        if (rmRes?.data && parentCertifiedRawMaterialCodes.length === 0) {
          setCertifiedRawMaterialCodes(rmRes.data);
        }
        
        if (baseRes?.data?.tcStandard && parentTcStandardList.length === 0) {
          const standards = Array.isArray(baseRes.data.tcStandard) ? baseRes.data.tcStandard : [];
          setTcStandardList(standards);
        }
      } catch (error) {
        console.error("Failed to fetch certified raw materials or TC standards", error);
        hasFetchedCertifiedRMCodes.current = false; // Reset on error to allow retry
        lastTeIdFetchedRef.current = "";
      }
    };
    fetchCertifiedData();
  }, [TE_ID, parentCertifiedRawMaterialCodes.length, parentTcStandardList.length]);

  // Memoize the product key to track changes without depending on entire array reference
  const productKey = useMemo(() => {
    if (!associatedProductNo || !Array.isArray(productData) || productData.length === 0) {
      return null;
    }
    const product = productData.find((p: any) => p.tcProductArticleNo === associatedProductNo);
    if (!product || !product.productId || !product.shipmentId) {
      return null;
    }
    // Return a string key instead of an object to avoid reference equality issues
    return `${product.productId}-${product.shipmentId}-${associatedProductNo}`;
  }, [associatedProductNo, productData]);

  // Memoize the selected product for use in calculations
  const selectedProduct = useMemo(() => {
    if (!associatedProductNo || !Array.isArray(productData) || productData.length === 0) {
      return null;
    }
    return productData.find((p: any) => p.tcProductArticleNo === associatedProductNo) || null;
  }, [associatedProductNo, productData]);

  // Fetch product raw material information when associated product changes
  useEffect(() => {
    // Early return if no associated product or TE_ID
    if (!associatedProductNo || !TE_ID) {
      setProductRawMaterialInfo([]);
      lastFetchedProductRef.current = "";
      return;
    }

    // Early return if product not found or missing required IDs
    if (!productKey || !selectedProduct || !selectedProduct.productId || !selectedProduct.shipmentId) {
      setProductRawMaterialInfo([]);
      lastFetchedProductRef.current = "";
      return;
    }

    // Skip if we've already fetched data for this exact product
    if (lastFetchedProductRef.current === productKey) {
      return;
    }

    const fetchProductRawMaterials = async () => {
      try {
        lastFetchedProductRef.current = productKey; // Set before fetch to prevent concurrent calls
        const response = await executeGetRef.current(TCAppConfig.GET_PRODUCT_INFO_V4, {
          tcId: TE_ID,
          productId: selectedProduct.productId,
          shipmentId: selectedProduct.shipmentId,
        });
        const productInfo = response?.data?.tcProductInformation?.[0];
        if (productInfo?.tcProductRawMaterialInformation && Array.isArray(productInfo.tcProductRawMaterialInformation)) {
          setProductRawMaterialInfo(productInfo.tcProductRawMaterialInformation);
        } else {
          setProductRawMaterialInfo([]);
        }
      } catch (error) {
        console.error("Failed to fetch product raw material information", error);
        setProductRawMaterialInfo([]);
        lastFetchedProductRef.current = ""; // Reset on error to allow retry
      }
    };

    fetchProductRawMaterials();
  }, [associatedProductNo, TE_ID, productKey, selectedProduct]);

  const [data, setData] = useState<TCComponentCertifiedWeightInformation[]>(
    productInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TCComponentCertifiedWeightInformation[]>(
    productInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("component-details");
  const [allLabelGrades, setAllLabelGrades] = useState<LabelGradeOption[]>([]);
  const [labelGrades, setLabelGrades] = useState<LabelGradeOption[]>([]);


  const { isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();

  // Fetch label grades via API (do not rely on store state)
  useEffect(() => {
    const fetchLabelGrades = async () => {
      try {
        const result: any = await dtrackItService.getStandradLabelGrade();
        const lblGrades = result?.data?.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          description: t.description_1,
        })) || [];
        setAllLabelGrades(lblGrades);
      } catch (err) {
        Utills.cl(`Error in fetching label grades ${err}`);
        setAllLabelGrades([]);
      }
    };

    fetchLabelGrades();
  }, []);

  // Filter label grades based on certified TC standards
  useEffect(() => {
    if (!allLabelGrades.length) {
      setLabelGrades([]);
      return;
    }

    // If no TC standards loaded yet, show all label grades
    // If no TC standards loaded yet, show all label grades
    if (!tcStandardList.length) {
      setLabelGrades(allLabelGrades);
      return;
    }

    // Build lowercase tokens from certified TC standards (split on '/', ',', whitespace)
    const tcStandardTokens = tcStandardList
      .map((s) => String(s).toLowerCase())
      .flatMap((s) =>
        s
          .split(/[\/,]/)
          .map((p) => p.trim())
          .filter(Boolean)
      );

    // Keep label grades where the label/value text contains any certified standard token
    const filtered = allLabelGrades.filter((opt) => {
      const text = `${opt.label} ${opt.value}`.toLowerCase();
      return tcStandardTokens.some((token) => token && text.includes(token));
    });

    setLabelGrades(filtered);
  }, [allLabelGrades, tcStandardList]);

  // Helper: Extract standard from component claims category / label grade code
  // No hardcoding of specific standards; derive by matching known TC standards in the text.
  const extractStandard = (code: string): string => {
    if (!code) return "";
    const normalizedCode = code.trim();
    const lower = normalizedCode.toLowerCase();
    if (lower === "na") return "na";

    // Build tokens from tcStandardList (dynamic, not hardcoded)
    const tcTokens = tcStandardList
      .map((s) => String(s).toLowerCase())
      .flatMap((s) =>
        s
          .split(/[\/,]/)
          .map((p) => p.trim())
          .filter(Boolean)
      );

    // Find the first token that appears in the code text
    const matched = tcTokens.find((tok) => tok && lower.includes(tok));
    if (matched) return matched.toUpperCase();

    // Fallback: take the first segment before space or hyphen
    const parts = normalizedCode.split(/[\s-]/);
    return parts[0]?.trim().toUpperCase() || normalizedCode.toUpperCase();
  };

  // Define Yup validation schema for each row - conditional based on componentNo
  // Use useMemo to recreate schema when data or editingRowIndex changes
  const validationSchema = useMemo(() => Yup.object().shape({
    tcProductComponentClaimsCategory: Yup.string()
      .when([], {
        is: () => componentNo && componentNo.toLowerCase().trim() !== "na",
        then: (schema) => 
          schema
            .required("Component Claims Category is required when Component No is not 'na'")
            .test(
              'unique-standard',
              'Only 1 claims category per standard is allowed for a component',
              function (value) {
                if (!value || editingRowIndex === -1) return true;
                const selectedStandard = extractStandard(value);
                const selectedRaw = value.trim();
                // Allow multiple rows with "na" / no real standard
                if (!selectedStandard || selectedStandard.toLowerCase() === "na") {
                  return true;
                }
                
                // Allow the same exact option multiple times (e.g., "GRS" with "GRS")
                // but block mixing variants of the same standard (e.g., "GRS" vs "GRS (No Label)")
                const hasVariantClash = data.some((row, index) => {
                  if (index === editingRowIndex) return false; // Skip current editing row
                  if (!row.tcProductComponentClaimsCategory) return false;
                  const rowValue = row.tcProductComponentClaimsCategory.trim();
                  const rowStandard = extractStandard(rowValue);
                  return rowStandard === selectedStandard && rowValue !== selectedRaw;
                });
                
                return !hasVariantClash;
              }
            ),
        otherwise: (schema) => 
          schema
            .nullable()
            .test(
              'unique-standard',
              'Only 1 claims category per standard is allowed for a component',
              function (value) {
                if (!value || editingRowIndex === -1) return true;
                const selectedStandard = extractStandard(value);
                const selectedRaw = value.trim();
                // Allow multiple rows with "na" / no real standard
                if (!selectedStandard || selectedStandard.toLowerCase() === "na") {
                  return true;
                }
                
                // Allow the same exact option multiple times (e.g., "GRS" with "GRS")
                // but block mixing variants of the same standard (e.g., "GRS" vs "GRS (No Label)")
                const hasVariantClash = data.some((row, index) => {
                  if (index === editingRowIndex) return false; // Skip current editing row
                  if (!row.tcProductComponentClaimsCategory) return false;
                  const rowValue = row.tcProductComponentClaimsCategory.trim();
                  const rowStandard = extractStandard(rowValue);
                  return rowStandard === selectedStandard && rowValue !== selectedRaw;
                });
                
                return !hasVariantClash;
              }
            ),
      }),
    tcProductComponentCertifiedWeight: Yup.number()
      .when([], {
        is: () => componentNo && componentNo.toLowerCase().trim() !== "na",
        then: (schema) =>
          schema
            .required("Component Certified Weight is required when Component No is not 'na'")
            .typeError("Certified Weight must be a number")
            .min(0, "Weight must be positive")
            .test(
              "is-five-decimal",
              "Weight must have at most 5 decimal places",
              (value) => value == null || /^\d+(\.\d{1,5})?$/.test(value.toString())
            ),
        otherwise: (schema) =>
          schema
            .nullable()
            .typeError("Certified Weight must be a number")
            .min(0, "Weight must be positive")
            .test(
              "is-five-decimal",
              "Weight must have at most 5 decimal places",
              (value) => value == null || /^\d+(\.\d{1,5})?$/.test(value.toString())
            ),
      }),
  }), [componentNo, data, editingRowIndex]);

  // Helper: Check if Raw Material is Certified
  const isCertifiedRM = (code: string): boolean => {
    if (!code || !certifiedRawMaterialCodes.length || !tcStandardList.length) return false;
    const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === code);
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;
    const standards = rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");
    if (!standards.length) return false;
    const tcStandardsLower = tcStandardList.map((s) => String(s).toLowerCase());
    return standards.some((std) => tcStandardsLower.includes(std.toLowerCase()));
  };

  // Calculate sum of component certified weights
  const componentCertifiedWeightSum = useMemo(() => {
    return data.reduce((sum, item) => {
      const weight = Number(item.tcProductComponentCertifiedWeight || 0);
      return sum + (isNaN(weight) ? 0 : weight);
    }, 0);
  }, [data]);

  // Calculate product certified weight (similar to product-certified-weight.tsx)
  const productCertifiedWeight = useMemo(() => {
    if (!selectedProduct || !productRawMaterialInfo.length) {
      return 0;
    }

    const netShippingWeight = Number(selectedProduct.tcNetShippingWeight || 0);
    const supplementaryWeight = Number(selectedProduct.tcProductSupplementaryWeight || 0);
    
    const rawMaterialPercentage = productRawMaterialInfo.reduce(
      (acc, item) => {
        const isCertified = isCertifiedRM(item.tcProductRawMaterialCode);
        const percentage = Number(item.tcProductRawMaterialPercentage || 0);
        return acc + (isCertified ? percentage : 0);
      },
      0
    );

    const certifiedWeight = ((netShippingWeight - supplementaryWeight) * rawMaterialPercentage) / 100;
    return Number(certifiedWeight.toFixed(2));
  }, [selectedProduct, productRawMaterialInfo, certifiedRawMaterialCodes, tcStandardList]);

  // Calculate certified weight breakdown by RM code
  const certifiedWeightBreakdown = useMemo(() => {
    const netWeight = Number(componentNetShippingWeight || 0);
    if (!rawMaterialInfoData?.length || !netWeight || !certifiedRawMaterialCodes.length || !tcStandardList.length) {
      return [];
    }

    // Group certified weights by standard (total weight per standard across the component)
    const weightsByStandard = new Map<string, number>();
    data.forEach((item: any) => {
      const standard = extractStandard(item.tcProductComponentClaimsCategory || "");
      if (standard) {
        const weight = Number(item.tcProductComponentCertifiedWeight || 0);
        weightsByStandard.set(standard, (weightsByStandard.get(standard) || 0) + weight);
      }
    });

    // Precompute certified raw materials and their standards
    type CertifiedRM = {
      rmCode: string;
      rmDescription: string;
      percentage: number;
      rmStandards: string[];
    };

    const certifiedRMs: CertifiedRM[] = [];

    rawMaterialInfoData.forEach((raw: any) => {
      const rmCode = raw.tcProductComponentRawMaterialCode;
      if (!rmCode || !isCertifiedRM(rmCode)) return;

      const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === rmCode);
      const rawStandards: string = mapping?.standard || "";
      if (!rawStandards || rawStandards.toUpperCase() === "NA") return;

      const rmStandards = rawStandards
        .split("/")
        .map((s: string) => s.trim().toUpperCase())
        .filter((s: string) => s && s.toUpperCase() !== "NA");

      const percentage = Number(raw.tcProductComponentRawMaterialPercentage || 0);

      certifiedRMs.push({
        rmCode,
        rmDescription: raw.tcProductComponentRawMaterialCodeDescription || rmCode,
        percentage,
        rmStandards,
      });
    });

    if (!certifiedRMs.length) {
      return [];
    }

    // For each standard, calculate the total RM percentage that carries this standard.
    // This lets us apportion the standard's certified weight fairly across RMs.
    const standardPercentageTotals = new Map<string, number>();
    certifiedRMs.forEach((rm) => {
      rm.rmStandards.forEach((std) => {
        standardPercentageTotals.set(
          std,
          (standardPercentageTotals.get(std) || 0) + rm.percentage
        );
      });
    });

    // Calculate breakdown for each certified RM with apportioned weights
    const breakdown: Array<{
      rmCode: string;
      rmDescription: string;
      percentage: number;
      expectedWeight: number;
      actualWeight: number;
      standards: string[];
      isValid: boolean;
    }> = [];

    certifiedRMs.forEach((rm) => {
      const { rmCode, rmDescription, percentage, rmStandards } = rm;
      const expectedWeight = (netWeight * percentage) / 100;

      // Apportion certified weights by standard based on RM percentage share
      let actualWeight = 0;
      rmStandards.forEach((rmStd: string) => {
        const stdWeight = weightsByStandard.get(rmStd) || 0;
        const totalPctForStd = standardPercentageTotals.get(rmStd) || 0;
        if (!stdWeight || !totalPctForStd) return;

        const share = percentage / totalPctForStd; // RM's share of this standard
        actualWeight += stdWeight * share;
      });

      breakdown.push({
        rmCode,
        rmDescription,
        percentage,
        expectedWeight: Number(expectedWeight.toFixed(2)),
        actualWeight: Number(actualWeight.toFixed(2)),
        standards: rmStandards,
        isValid: actualWeight <= expectedWeight + 0.01, // Allow 0.01 tolerance
      });
    });

    return breakdown;
  }, [rawMaterialInfoData, componentNetShippingWeight, data, certifiedRawMaterialCodes, tcStandardList]);

  const formik = useFormik<TCComponentCertifiedWeightInformation>({
    initialValues: {
      tcProductComponentClaimsCategory: "",
      tcProductComponentCertifiedWeight: 0,
      tcProductComponentClaimsCategoryDescription:""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    const parsedValue =
      columnId === "tcProductComponentCertifiedWeight"
        ? value === "" ? null : Number(value) // string → number | null
        : value;
  
    formik.setFieldValue(columnId, parsedValue);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: parsedValue } : row
      )
    );
  };
  

  useEffect(() => {
    const dataWithOrder = data.map((item: any, index) => ({
      ...item,
      tcProductComponentStandardLabelOrder: index + 1,
    }));
    updateField("standard-info", dataWithOrder);
  }, [data, updateField])

  const handleAddNewRow = () => {
    const newRow: TCComponentCertifiedWeightInformation = {
      tcProductComponentClaimsCategory: "",
      tcProductComponentCertifiedWeight: 0,
      tcProductComponentClaimsCategoryDescription:""
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
   setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === -1) return;

    // Validate the form before saving
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // Show notification for validation errors
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        notify(errorMessages[0] as string, "error");
      }
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setProductInfoData(newData); // Update parent with current data
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<TCComponentCertifiedWeightInformation>[] = [
    {
      accessorKey: "tcProductComponentClaimsCategory",
      header: "Component Claims Category",
      Header: () => {
        const isRequired = componentNo && componentNo.toLowerCase().trim() !== "na";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Component Claims Category
          </span>
        );
      },
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="tcProductComponentClaimsCategory"
          value={value}
          enableSearch={true}
          options={labelGrades}
          placeholder="Select Component Claims Category"
          onChange={(e) => {
            const selectedValue = e.target.value;
            const selectedOption = labelGrades.find((opt: any) => opt.value === selectedValue);
            const description: any = selectedOption?.label ?? "";
      
            // Update Formik
            onChange(selectedValue);
            formik.setFieldValue("tcProductComponentClaimsCategoryDescription", description);
            formik.setFieldValue("tcProductComponentClaimsCategory", selectedValue);
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductComponentCertifiedWeight",
      header: "Component Certified Weight (kg)",
      Header: () => {
        const isRequired = componentNo && componentNo.toLowerCase().trim() !== "na";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Component Certified Weight (kg)
          </span>
        );
      },
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductComponentCertifiedWeight"
          value={value ?? ""} //  handle null safely
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} // convert to number | null
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),      
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setProductInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        {/* Component Certified Weight Summary Info */}
        {associatedProductNo && (
          <Box mb={3}>
            <Box mt={1}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Component Certified Weight Sum: </span>
                {componentCertifiedWeightSum.toFixed(2)} kg
                {productCertifiedWeight > 0 && (
                  <>
                    {" = "}
                    <span style={{ fontWeight: 600 }}>Product Certified Weight: </span>
                    {productCertifiedWeight.toFixed(2)} kg
                  </>
                )}
              </Typography>
            </Box>
            
            {/* Certified Weight Breakdown by Raw Material */}
            {certifiedWeightBreakdown.length > 0 && (
              <Box mt={2}>
                <Typography fontWeight="600" mb={1}>
                  Certified Weight Breakdown by Raw Material:
                </Typography>
                {certifiedWeightBreakdown.map((item, idx) => (
                  <Box key={idx} ml={2} mb={1}>
                    <Typography 
                      component="div"
                      color={item.isValid ? "text.primary" : "error.main"}
                      sx={{ fontWeight: item.isValid ? 400 : 600 }}
                    >
                      <span style={{ fontWeight: 600 }}>{item.rmDescription} ({item.rmCode}):</span>{" "}
                      Actual: {item.actualWeight.toFixed(2)} kg / Expected Max: {item.expectedWeight.toFixed(2)} kg 
                      ({item.percentage}% of {componentNetShippingWeight ? Number(componentNetShippingWeight).toFixed(2) : "0.00"} kg)
                      {!item.isValid && (
                        <span style={{ color: "red", marginLeft: "8px" }}>
                          ⚠ Exceeds limit by {(item.actualWeight - item.expectedWeight).toFixed(2)} kg
                        </span>
                      )}
                      {item.standards.length > 0 && (
                        <Typography variant="caption" display="block" ml={2} color="text.secondary">
                          Standards: [{item.standards.join(", ")}]
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            onClick={handleAddNewRow}
            disabled={!formik.isValid || isRestrictPopUpSave}
            sx={{ mb: 2 }}
          >
            Add Product Component Certified Weight Info
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default ProductComponent;
